import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { submitContactForm, fetchGeolocation } from '../services/api';
import { toast } from 'sonner';

const countryCodes = [
  { code: '+1', country: 'US/CA', countryCode: 'US' },
  { code: '+44', country: 'UK', countryCode: 'GB' },
  { code: '+91', country: 'IN', countryCode: 'IN' },
  { code: '+61', country: 'AU', countryCode: 'AU' },
  { code: '+81', country: 'JP', countryCode: 'JP' },
  { code: '+31', country: 'NL', countryCode: 'NL' },
  { code: '+92', country: 'PK', countryCode: 'PK' },
  { code: '+49', country: 'DE', countryCode: 'DE' },
  { code: '+33', country: 'FR', countryCode: 'FR' },
  { code: '+971', country: 'AE', countryCode: 'AE' }
];

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [geoData, setGeoData] = useState(null);

  // Fetch geolocation when modal opens
  useEffect(() => {
    if (isOpen && !geoData) {
      fetchGeolocation()
        .then(data => {
          if (data && data.isSuccess && data.data) {
            setGeoData(data);
            // Auto-set country based on geolocation
            const countryCode = data.data.countryCode;
            const matchedCountry = countryCodes.find(c => c.countryCode === countryCode);
            if (matchedCountry) {
              setFormData(prev => ({
                ...prev,
                countryCode: matchedCountry.code,
                country: matchedCountry.countryCode
              }));
            }
          }
        })
        .catch(error => {
          console.error('Failed to fetch geolocation:', error);
        });
    }
  }, [isOpen, geoData]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{7,15}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be between 7 and 15 digits';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData({ ...formData, phone: value });
  };

  const handleCountryCodeChange = (e) => {
    const selectedCode = e.target.value;
    const matchedCountry = countryCodes.find(c => c.code === selectedCode);
    setFormData({ 
      ...formData, 
      countryCode: selectedCode,
      country: matchedCountry?.countryCode || 'US'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitContactForm(formData, geoData);
      
      toast.success(result.data?.message || 'Form submitted successfully!');
      
      // Form will redirect automatically on success
      // No need to reset or close as user will be redirected
    } catch (error) {
      toast.error(error.message || "Failed to submit form. Please try again.");
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Get Legal Help</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-5">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="John Doe"
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="john@example.com"
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          {/* Phone */}
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number *
            </label>
            <div className="flex space-x-2">
              <select
                value={formData.countryCode}
                onChange={handleCountryCodeChange}
                className="px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none bg-white"
              >
                {countryCodes.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                className={`flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="1234567890"
              />
            </div>
            {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
          </div>

          {/* Message */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
              Describe Your Legal Needs *
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows="4"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-brown-600 focus:border-brown-600 outline-none transition-colors resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Tell us about your legal situation..."
            />
            {errors.message && <p className="mt-1 text-sm text-red-600">{errors.message}</p>}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-brown-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-brown-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </button>

          <p className="text-xs text-gray-500 text-center">
            By submitting this form, you agree to our Terms of Service and Privacy Policy.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactFormModal;
